/**
 * Lógica pura da Análise de fluxograma: montagem do grafo, leitura dos gatilhos
 * e leitura do catálogo de ações. Entram dados, saem dados — sem DOM e sem
 * estado de tela. O componente cuida do template, do estado e da geometria.
 */

export const CORES = ['#3a3e81', '#2298a3', '#E4572E', '#1E9D6B', '#B9791A', '#7A3FC0', '#C0392B', '#2F6FB0'];

export const KIND = {
  email:     { icon: 'comments',        label: 'Envia e-mail',        cor: '#2298a3' },
  move:      { icon: 'exchange-alt',    label: 'Movimenta ticket',    cor: '#3a3e81' },
  history:   { icon: 'clipboard-list',  label: 'Registra histórico',  cor: '#0E8A8A' },
  notify:    { icon: 'bullhorn',        label: 'Cria aviso interno',  cor: '#B9791A' },
  webhook:   { icon: 'globe',           label: 'Chama webhook',       cor: '#7A3FC0' },
  ticket:    { icon: 'ticket-alt',      label: 'Cria ticket',         cor: '#1E9D6B' },
  devolucao: { icon: 'ticket-alt',      label: 'Gera devolução',      cor: '#C0392B' },
  sql:       { icon: 'database',        label: 'SQL / Procedure',     cor: '#C0392B' },
  job:       { icon: 'cogs',            label: 'Integração (JOB)',    cor: '#6c757d' },
};

export const EVENTOS = {
  TicketCriado: 'na abertura do ticket',
  TicketAtualizado: 'ao atualizar o ticket',
  TicketMensagemEnviada: 'quando o cliente envia mensagem/anexo',
  TicketEtapaAvancada: 'ao avançar de etapa',
  TicketEtapaRetrocedida: 'ao retroceder de etapa',
  TicketRetrocedido: 'ao retroceder de etapa',
  TicketItensAlterados: 'quando os itens são alterados',
  TicketProdutoNegado: 'quando um produto é negado',
  TicketTodosProdutosAnalisados: 'quando todos os produtos são analisados',
  TicketFinalizado: 'ao finalizar o ticket',
};

// atributos que a etapa carrega por tipo de ticket (uma linha por tipo+chave)
export const CAMPOS_ETAPA = ['requerimento', 'aguarda', 'final', 'sla', 'setor'];

export function etapasDoTipo(tipoTicketEtapa, tipoId) {
  return (tipoTicketEtapa || [])
    .filter((e) => e.tipo_ticket_id === tipoId)
    .sort((a, b) => a.ordem - b.ordem);
}

export function minOrdem(node) {
  if (node.root) return -1;
  if (node.emptyTipo) return 99;
  const os = Object.values(node.tipos).map((t) => t.ordem);
  return os.length ? Math.min(...os) : 99;
}

export function computeLayers(nodes, edges) {
  const preds = {};
  edges.forEach((e) => { (preds[e.b] = preds[e.b] || []).push(e.a); });
  const layer = { root: 0 };
  let changed = true, guard = 0;
  while (changed && guard++ < 80) {
    changed = false;
    Object.keys(nodes).forEach((n) => {
      if (n === 'root') return;
      const ps = preds[n] || [];
      const v = ps.length ? Math.max(...ps.map((p) => (layer[p] !== undefined ? layer[p] : 0))) + 1 : 1;
      if (layer[n] !== v) { layer[n] = v; changed = true; }
    });
  }
  return layer;
}

/**
 * Monta nós, arestas sequenciais, transições condicionais e colunas do diagrama.
 * Etapas com a mesma chave viram um nó único; os atributos ficam por tipo em
 * `node.tipos[tipoId]`, porque a mesma chave pode ser final em um tipo e
 * intermediária em outro.
 */
export function montaGrafo({ tipoTicket = [], tipoTicketEtapa = [], condicionais = [], nomeSetor = () => null } = {}) {
  const nodes = { root: { key: 'root', root: true, tipos: {} } };
  const edgeMap = {};
  const addEdge = (a, b, tipoId) => {
    const k = a + '|' + b;
    if (!edgeMap[k]) edgeMap[k] = { a, b, types: [] };
    if (!edgeMap[k].types.includes(tipoId)) edgeMap[k].types.push(tipoId);
  };

  tipoTicket.forEach((tipo) => {
    const etapas = etapasDoTipo(tipoTicketEtapa, tipo.id);
    if (!etapas.length) {
      const ek = '__EMPTY__' + tipo.id;
      nodes[ek] = { key: ek, chave: '', desc: 'Sem etapas', emptyTipo: tipo.id, tipos: {} };
      addEdge('root', ek, tipo.id);
      return;
    }
    let prev = 'root';
    etapas.forEach((et) => {
      const key = et.chave || ('ETAPA_' + et.id);
      if (!nodes[key]) {
        nodes[key] = { key, chave: et.chave, desc: et.desc, tipos: {} };
      }
      nodes[key].tipos[tipo.id] = {
        ordem: et.ordem,
        etapaId: et.id,
        requerimento: et.requerimento || null,
        aguarda: !!Number(et.aguarda_cliente),
        final: !!Number(et.final),
        sla: et.sla_maximo || null,
        setor: nomeSetor(et.setor_id),
      };
      addEdge(prev, key, tipo.id);
      prev = key;
    });
  });

  // camadas (longest path) + colunas — só as transições sequenciais entram no
  // cálculo: as condicionais podem retroceder e criariam ciclo
  const layer = computeLayers(nodes, Object.values(edgeMap));
  Object.keys(nodes).forEach((k) => { nodes[k].layer = layer[k]; });

  // transições condicionais (ticket_etapa_condicional) sobre o grafo já montado
  const condEdges = [];
  const condOrfas = [];
  (condicionais || []).forEach((c) => {
    const tipoId = Number(c.tipo_ticket_id);
    const a = c.etapa_atual_chave;
    const b = c.proxima_etapa_chave;
    const okA = a && nodes[a] && nodes[a].tipos && nodes[a].tipos[tipoId];
    const okB = b && nodes[b] && nodes[b].tipos && nodes[b].tipos[tipoId];
    if (!okA || !okB) { condOrfas.push(c); return; }
    // já é a transição natural do fluxo -> não duplica a aresta
    if (edgeMap[a + '|' + b] && edgeMap[a + '|' + b].types.includes(tipoId)) return;
    const k = a + '|' + b + '|' + tipoId;
    if (condEdges.some((e) => e.k === k)) return;
    condEdges.push({ k, a, b, tipoId, types: [tipoId], cond: true });
  });

  // arestas que "pulam" colunas -> roteadas por um canal abaixo das caixas
  const skip = (e) => (layer[e.b] - layer[e.a]) > 1;
  const nSkip = Object.values(edgeMap).filter(skip).length + condEdges.filter(skip).length;
  const padBottom = nSkip ? (nSkip * 14 + 22) : 0;
  // condicionais que voltam (ou ficam na mesma coluna) -> canal acima das caixas
  const nBack = condEdges.filter((e) => (layer[e.b] - layer[e.a]) <= 0).length;
  const padTop = nBack ? (nBack * 14 + 22) : 0;

  const maxL = Math.max(...Object.values(layer));
  const colunas = [];
  for (let l = 0; l <= maxL; l++) {
    const keys = Object.keys(nodes)
      .filter((k) => layer[k] === l)
      .sort((a, b) => minOrdem(nodes[a]) - minOrdem(nodes[b]));
    if (keys.length) colunas.push(keys);
  }

  return {
    nodes,
    colunas,
    condEdges,
    condOrfas,
    padBottom,
    padTop,
    baseEdges: Object.values(edgeMap).concat(condEdges),
  };
}

// `false`, `''` e `undefined` são a mesma ausência de valor para efeito de divergência
function normalizaFlag(v) {
  return (v === false || v === undefined || v === '') ? null : v;
}

/**
 * Valor dos atributos da etapa para exibição.
 * Com um tipo em foco vale a linha daquele tipo. Sem foco, devolve o valor que
 * existe e marca `divergente` quando os tipos não concordam, com a lista dos
 * tipos em que o atributo vale — a tela não pode afirmar "etapa final" para um
 * tipo em que ela não é final.
 */
export function flagsDoNode(node, activeType = null, nomeTipo = () => '') {
  const tipos = (node && node.tipos) || {};
  const entradas = Object.entries(tipos);
  const linhaFoco = (activeType != null && tipos[activeType]) ? tipos[activeType] : null;
  const out = {};

  CAMPOS_ETAPA.forEach((campo) => {
    if (linhaFoco) {
      out[campo] = { valor: normalizaFlag(linhaFoco[campo]), divergente: false, tipos: [] };
      return;
    }
    const valores = entradas.map(([tid, info]) => [Number(tid), normalizaFlag(info[campo])]);
    const com = valores.filter(([, v]) => v !== null);
    out[campo] = {
      valor: com.length ? com[0][1] : null,
      divergente: new Set(valores.map(([, v]) => v)).size > 1,
      tipos: com.map(([tid]) => nomeTipo(tid)).filter(Boolean),
    };
  });

  return out;
}

export function montaCondLista(condEdges, { nomeNode, nomeTipo, corTipo }) {
  return (condEdges || []).map((e) => ({
    origem:      e.a,
    destino:     e.b,
    tipoId:      e.tipoId,
    origemNome:  nomeNode(e.a),
    destinoNome: nomeNode(e.b),
    tipoNome:    nomeTipo(e.tipoId),
    cor:         corTipo(e.tipoId),
  }));
}

/* ---------- gatilhos ---------- */

// a quais tipos/etapas o gatilho se aplica, a partir das regras (condição)
export function parseGatilho(g, { tipoTicket = [], tipoTicketEtapa = [] } = {}) {
  const tipoIds = new Set();
  const etapaChaves = new Set();
  ((g && g.regras) || []).forEach((r) => {
    const col = String(r.coluna || '').trim();
    const vals = String(r.valor == null ? '' : r.valor).split(',').map((s) => s.trim()).filter(Boolean);
    if (col === 'tipo.chave') {
      vals.forEach((v) => { const t = tipoTicket.find((x) => x.chave === v); if (t) tipoIds.add(t.id); });
    } else if (col === 'tipo.id' || col === 'tipo_ticket_id') {
      vals.forEach((v) => tipoIds.add(Number(v)));
    } else if (/\.chave$/.test(col) && /(etapa|proxima)/i.test(col)) {
      vals.forEach((v) => etapaChaves.add(v));
    }
  });
  // etapas referenciadas -> tipos que as possuem
  etapaChaves.forEach((ch) => {
    tipoTicketEtapa.filter((e) => e.chave === ch).forEach((e) => tipoIds.add(e.tipo_ticket_id));
  });
  const global = tipoIds.size === 0 && etapaChaves.size === 0;
  return { tipoIds: [...tipoIds], etapaChaves: [...etapaChaves], global };
}

export function classify(a) {
  const acao = a || {};
  const tipo = acao.acao_tipo_slug || acao.tipo_execucao || acao.tipo || '';
  // shape real: a.acao = { class_method_or_sql, params }
  const classe = (acao.acao && (acao.acao.class_method_or_sql || acao.acao)) || acao.classe || '';
  if (tipo === 'send_email') return 'email';
  if (tipo === 'notification') return 'notify';
  if (tipo === 'call_webhook') return 'webhook';
  if (tipo === 'create_ticket') return 'ticket';
  if (tipo === 'SQL' || tipo === 'PROCEDURE') return 'sql';
  if (/Avanca|Retroced|Movimenta/i.test(classe)) return 'move';
  if (/Historico/i.test(classe)) return 'history';
  if (/Devolucao|Espelho|Credito/i.test(classe)) return 'devolucao';
  if (/Email/i.test(classe)) return 'email';
  if (/Notifica|Aviso/i.test(classe)) return 'notify';
  if (/Webhook/i.test(classe)) return 'webhook';
  if (/CriaTicket|CreateTicket/i.test(classe)) return 'ticket';
  return 'job';
}

export function acoesDoGatilho(g) {
  return (g && (g.acoes || g.execucoes)) || [];
}

export function acaoCodigo(a) {
  return (a && ((a.acao && a.acao.class_method_or_sql) || a.acao)) || '—';
}

// catálogo de ações JOB (/admin/sac/ticket-acao-execucao/acao): label e parâmetros
export function catalogoAcao(catalogo, classMethod) {
  return (catalogo || []).find((a) => a.class_method_or_sql === classMethod) || null;
}

export function acaoTitulo(a, catalogo) {
  const kind = KIND[classify(a)];
  if (a && a.tipo_execucao && a.tipo_execucao !== 'JOB') return kind.label;
  const codigo = acaoCodigo(a);
  const cat = catalogoAcao(catalogo, codigo);
  if (cat && cat.label) return cat.label;
  const partes = String(codigo).split('\\');
  return partes[partes.length - 1] || kind.label;
}

// casa os valores configurados (índice 1..N) com os nomes dos parâmetros do catálogo
export function acaoParams(a, catalogo) {
  const valores = (a && a.acao && a.acao.params) || {};
  const cat = catalogoAcao(catalogo, acaoCodigo(a));
  const nomes = (cat && cat.params) || {};
  return Object.keys(valores).map((k) => ({
    nome: (nomes[k] && nomes[k].name) || `param ${k}`,
    valor: valores[k],
  }));
}

export function acaoLabelGatilho(g) {
  const acoes = acoesDoGatilho(g);
  if (!acoes.length) return 'Sem ações';
  const label = KIND[classify(acoes[0])].label;
  return acoes.length > 1 ? `${label} +${acoes.length - 1}` : label;
}

export function condicaoLabel(dicionario, cond) {
  return (dicionario || {})[cond] || cond || '—';
}

export function eventoTexto(ev) {
  return EVENTOS[ev] || ev;
}
